import React from "react";
import { MapPin, Mail, Phone, Globe, MessageCircle, ExternalLink } from "lucide-react";

export const CampusSelector = () => {
  const campuses = [
    {
      id: "main",
      name: "Main Campus",
      location: "Takoradi",
      address: "P.O. Box 682, Takoradi",
      gpsAddress: "W62X+JPR Paa Grant Roundabouts, Palace Cl, Takoradi",
      gpsLink: "https://www.google.com/maps/search/W62X%2BJPR+Paa+Grant+Roundabouts+Palace+Cl+Takoradi+Ghana",
      phone: "(+233-31-22347)",
      mobile: "(+055-2184037)",
      whatsapp: "(+055-2184037)",
      email: "main@ycpschool.com",
      website: "www.ycpschool.com",
      color: "from-purple-600 to-violet-700",
      bgColor: "bg-gradient-to-br from-purple-600 to-violet-700",
      image: "https://customer-assets.emergentagent.com/job_248403c7-c2c6-44b2-9d63-d9aa2b4e99b6/artifacts/oiccwtov_WhatsApp%20Image%202026-01-20%20at%206.40.13%20AM%20%284%29%20-%20Copy.jpeg",
      mapUrl: "https://www.google.com/maps/search/Young+Christian+Preparatory+School+Takoradi+Ghana",
      mapEmbed: "https://www.openstreetmap.org/export/embed.html?bbox=-1.78%2C4.88%2C-1.73%2C4.92&layer=mapnik&marker=4.9016%2C-1.7557",
      lat: 4.9016,
      lng: -1.7557
    },
    {
      id: "annex",
      name: "Annex Campus",
      location: "Effia-Kuma, Takoradi",
      address: "P.O. Box 682, Lagos Town",
      gpsAddress: "WK-396-6047",
      gpsLink: "https://www.google.com/maps?q=4.910984,-1.775947",
      phone: "(+233-31-22347)",
      mobile: "(+055-2184037)",
      whatsapp: "(+055-2184037)",
      email: "annex@ycpschool.com",
      website: "www.ycpschool.com",
      color: "from-blue-600 to-blue-700",
      bgColor: "bg-gradient-to-br from-blue-600 to-blue-700",
      image: "https://customer-assets.emergentagent.com/job_248403c7-c2c6-44b2-9d63-d9aa2b4e99b6/artifacts/cezi2obt_WhatsApp%20Image%202026-01-20%20at%206.40.18%20AM.jpeg",
      mapUrl: "https://www.google.com/maps?q=4.910984,-1.775947",
      mapEmbed: "https://www.openstreetmap.org/export/embed.html?bbox=-1.782%2C4.905%2C-1.770%2C4.917&layer=mapnik&marker=4.910984%2C-1.775947",
      lat: 4.910984,
      lng: -1.775947
    }
  ];

  return (
    <section className="pt-0 pb-6 bg-gradient-to-b from-yellow-400 to-orange-400">
      <div className="max-w-5xl mx-auto px-4">
        <div className="flex justify-center mb-2 -mt-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_b5435c93-033f-4e57-8193-8df477e3abd8/artifacts/p9jfe1zg_358693407_106257442535698_4391661366190125414_n.jpg"
            alt="School Logo"
            className="w-80 h-80 md:w-96 md:h-96 object-contain"
          />
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-[#002147] mb-4 text-center">Our Campuses</h2>
        <p className="text-[#002147]/80 text-center mb-8 md:mb-10 max-w-2xl mx-auto text-sm md:text-base">
          Young Christian Preparatory School/ JHS operates two campuses in Takoradi to serve our growing community.
        </p>
        
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {campuses.map((campus) => (
            <div
              key={campus.id}
              className={`${campus.bgColor} rounded-2xl md:rounded-3xl overflow-hidden text-white transform transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl`}
              data-testid={`campus-${campus.id}`}
            >
              {/* Campus Image */}
              <img 
                src={campus.image}
                alt={campus.name}
                className="w-full h-32 md:h-40 object-cover"
              />
              
              <div className="p-6 md:p-8">
                <h3 className="text-xl md:text-2xl font-bold mb-4 md:mb-5">{campus.name}</h3>
                
                <div className="space-y-2 md:space-y-3">
                  <div className="flex items-center gap-3">
                    <MapPin size={16} className="text-white/80 flex-shrink-0" />
                    <span className="text-sm">{campus.address}</span>
                  </div>
                  {campus.gpsAddress && (
                    <div className="flex items-center gap-3">
                      <MapPin size={16} className="text-white/80 flex-shrink-0" />
                      <a
                        href={campus.gpsLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm underline hover:text-yellow-200 transition-colors cursor-pointer"
                        data-testid={`campus-${campus.id}-gps-link`}
                      >
                        GPS: {campus.gpsAddress}
                      </a>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Phone size={16} className="text-white/80 flex-shrink-0" />
                    <span className="text-sm">TEL: {campus.phone}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone size={16} className="text-white/80 flex-shrink-0" />
                    <span className="text-sm">Mobile: {campus.mobile}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MessageCircle size={16} className="text-white/80 flex-shrink-0" />
                    <span className="text-sm">WhatsApp: {campus.whatsapp}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail size={16} className="text-white/80 flex-shrink-0" />
                    <a href={`mailto:${campus.email}`} className="text-sm underline hover:no-underline">{campus.email}</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Globe size={16} className="text-white/80 flex-shrink-0" />
                    <a href={`https://${campus.website}`} target="_blank" rel="noopener noreferrer" className="text-sm underline hover:no-underline">{campus.website}</a>
                  </div>
                </div>

                {/* View on Google Maps Button */}
                <a
                  href={campus.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 w-full flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 border border-white/30 hover:border-white/50"
                  data-testid={`campus-${campus.id}-map-btn`}
                >
                  <MapPin size={18} />
                  View on Google Maps
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CampusSelector;
